/****  availability string format examples. -1 means not set so that will be defaulted

        "Availability_txt_sort":["1001,0,X,0,01-01-0001, 7",
          "1007,0,X,0,01-01-0001, 1",
          "1008,0,X,0,01-01-0001, 14",
          "1010,0,X,0,01-01-0001, 7",
          "1011,0,X,0,01-01-0001, 14",
          "1013,0,X,0,01-01-0001, 7",
          "1015,0,X,0,01-01-0001, 27",
          "1003,0,,0,01-01-0001, -1",
          "1012,0,,0,01-01-0001, -1",
          "1014,0,,0,01-01-0001, -1",
          "1018,0,,0,01-01-0001, -1",
          "1019,0,,0,01-01-0001, -1",
          "1020,0,,0,01-01-0001, -1",
          "1021,0,,0,01-01-0001, -1",
          "1022,0,,0,01-01-0001, -1",
          "1023,0,,0,01-01-0001, -1",
          "1025,0,,0,01-01-0001, -1",
          "1028,0,,0,01-01-0001, -1",
          "1030,0,,0,01-01-0001, -1"],
*/
import { OrderItem } from 'src/types/orderEntry';
import { LEAD_TIMES } from 'src/constants';
import moment from 'moment';

export function getItemAvailabilityRowForWarehouse(item: OrderItem, warehouseCode: string): string | null {
  const availability: string[] = item.availability;

  if (!availability || availability.length === 0) return null;
  if (!warehouseCode) return null;

  for (let i = 0; i < availability.length; i++) {
    const parts: string[] = availability[i].split(',');
    if (parts.length > 1) {
      const whCode = parts[0];
      if (whCode === warehouseCode) {
        return availability[i];
      }
    }
  }
  return null;
}

export function getGreatestLeadTimeOfUnavailableItems(
  items: OrderItem[],
  warehouseCode: string,
  orderType: string,
  orderQuote: string,
  isOpenOrderEdit: boolean,
  submitDate?: Date,
  sourcepros?: string
): [number | null, string | null] {
  if (orderType?.toLowerCase() !== 'w') return [null, null];
  if (!items || items.length === 0) return [null, null];
  if (!warehouseCode) return [null, null];

  //original source was edi so ignore the lead time calculation
  if (sourcepros && sourcepros.toLowerCase() === 'edi') {
    return [null, null];
  }

  let greatestLeadTime: number | null = null;
  let itemNumberOfGreatestLeadTime: string | null = null;

  const submitedDate = submitDate ? moment(submitDate) : null;

  for (const item of items) {
    const availability: string | null = getItemAvailabilityRowForWarehouse(item, warehouseCode);
    if (!availability) continue;
    if (item.quantity === undefined || item.quantity === null || item.quantity === 0) continue;

    const parts: string[] = availability.split(',');
    if (parts.length <= 5) continue;

    const stockStatus = parts[2].trim().toUpperCase();
    if (stockStatus !== 'O') continue; //BFG-3083 - We only want promise date logic for OAN items

    const availableQtyStr = parts[1].trim();
    const availableQty = parseInt(availableQtyStr, 10);

    if (isNaN(availableQty) || availableQty > 0) continue;

    //item.qtyShipped is availability already tied to the order. If it is >= quantity then it's already been allocated and no warning should happen
    if (item.qtyShipped && item.qtyShipped >= item.quantity) continue;

    const leadTimeNum = parseInt(parts[5].trim(), 10);

    let calculatedLeadTime: number;

    if (
      isOpenOrderEdit === true &&
      orderQuote.toLowerCase() === 'order' &&
      submitedDate &&
      item.promisedDate &&
      moment(item.promisedDate).isValid() &&
      !moment(item.promisedDate).isSame(moment('0001-01-01'))
    ) {
      // OPEN ORDER EDIT - Calculate lead time from the original order submission date
      // This preserves the "lead time clock" that started when the order was first submitted
      // Only for items have have already been submitted and have a promised date
      console.log('Calculating lead time based on original order submission date for item', item.itemNumber);
      const today = moment().startOf('day');
      const daysSinceOriginalOrder = today.diff(submitedDate.startOf('day'), 'days');

      // Get the effective lead time (from data or default)
      let itemLeadTime: number;
      if (!isNaN(leadTimeNum) && leadTimeNum >= 1) {
        itemLeadTime = leadTimeNum + LEAD_TIMES.ADDITIONAL_LEAD_TIME_DAYS;
      } else {
        itemLeadTime = LEAD_TIMES.DEFAULT_LEAD_TIME_DAYS;
      }

      // Subtract days that have already passed since original order
      calculatedLeadTime = itemLeadTime - daysSinceOriginalOrder;
      calculatedLeadTime = calculatedLeadTime >= 0 ? calculatedLeadTime : 0; // Can't be negative
    } else if (!isNaN(leadTimeNum) && leadTimeNum >= 1) {
      //Use lead time set on item data
      calculatedLeadTime = leadTimeNum + LEAD_TIMES.ADDITIONAL_LEAD_TIME_DAYS;
    } else {
      //Default Lead Time since lead time not set on data
      calculatedLeadTime = LEAD_TIMES.DEFAULT_LEAD_TIME_DAYS;
    }

    if (greatestLeadTime === null || calculatedLeadTime > greatestLeadTime) {
      greatestLeadTime = calculatedLeadTime;
      itemNumberOfGreatestLeadTime = item.itemNumber;
    }
  }

  return [greatestLeadTime, itemNumberOfGreatestLeadTime];
}